
import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PaginationComponent } from '../../../shared/pagination/pagination';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { Country } from '../../../core/models/country';
import { MatIconModule } from '@angular/material/icon';
import { LookupsApiService } from '../../../core/api-services/lookups-api.services';
import { TemplateApiService } from '../../../core/api-services/template-api.service';
import { ScholarshipApiService } from '../../../core/api-services/scholarship-api.service';
import { CreateScholarship, Scholarship, ScholarshipApplicationWindow, UpdateScholarship } from '../../../core/models/scholarship';
import { ScholarshipTemplate } from '../../../core/models/scholarship-template';
import { DocumentTypeFormat } from '../../../core/models/document-type-format';
import { AuthorizationService } from '../../../core/authorization/authorization.service';
import { Permissions } from '../../../core/authorization/permissions';
import { AllocationFrequency } from '../../../core/models/allocation-frequency';
import { ScholarshipCategory } from '../../../core/models/scholarship-category';
import { Eligibility } from '../../../core/models/eligibility';
import { interval, Subscription } from 'rxjs';
import { ScholarshipProvider } from '../../../core/models/scholarship-provider';
import { AdminCreationDraftApiService } from '../../../core/api-services/admin-creation-draft-api.service';
import { AdminCreationDraft } from '../../../core/models/admin-creation-draft';


@Component({
  selector: 'app-admin-scholarships',
  imports: [
    FormsModule,
    PaginationComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule
],
  templateUrl: './scholarships.html',
  styleUrl: './scholarships.scss',
})
export class AdminScholarshipsComponent implements OnInit, OnDestroy {
  readonly authorization = inject(AuthorizationService);
  readonly permissions = Permissions;

  /** Raised when the administrator opens a scholarship's administration page. */
  @Output() readonly viewScholarshipRequest = new EventEmitter<Scholarship>();
  private readonly lookupService = inject(LookupsApiService);
  private readonly templateApi = inject(TemplateApiService);
  private readonly scholarshipApi = inject(ScholarshipApiService);
  private readonly draftApi = inject(AdminCreationDraftApiService);
  private autosaveSubscription?: Subscription;
  private lastSavedDraftJson = '';

  saveMessage = '';
  errorMessage = '';
  showScholarshipModal = false;
  showDeleteConfirm = false;
  isEditing = false;
  editingScholarshipId: number = 0;
  scholarshipToDeleteId: number = 0;
  savingApplicationWindowId: number = 0;
  scholarshipsCount: number = 0;
  searchEligibiltiesControl = new FormControl('');

  scholarships: Scholarship[] = [];
  templates: ScholarshipTemplate[] = [];
  countries: Country[] = [];
  allEligibilities: Eligibility[] = [];
  selectedEligibilities: number[] = [];
  scholarshipProviders: ScholarshipProvider[] = [];

  code: string = '';
  title = '';
  country: Country = null!;
  countryId: number = 0;
  description: string = '';
  templateDescription: string = '';
  overview: string = '';
  selectedTemplateId: number = 0;
  fundingAmount: number = null!;
  applicationProcess: string = '';
  scholarshipLevel = '';
  deadline: string = '';
  minDeadline: any = null!;
  applicationUrl: string = '';
  imageUrl: string = '';
  allocationFrequencies: AllocationFrequency[] = [];
  selectedAllocationFrequencyId: number = 0;
  scholarshipCategories: ScholarshipCategory[] = [];
  selectedCategoryId: number = 0;
  providerId: number = 0;
  availableDraft?: AdminCreationDraft<ScholarshipDraftPayload>;
  isSavingDraft = false;
  draftMessage = '';

  currentPage = 1;
  readonly pageSize = 5;

  ngOnInit(): void {
    this.templateApi.getAllScholarshipTemplates().subscribe((data: ScholarshipTemplate[]) => {
      this.templates = data;
    });

    this.lookupService.getCountries().subscribe((data: Country[]) => {
      this.countries = data;
    });

    this.loadScholarships();

    this.lookupService.getAllocationFrequencies().subscribe((data: AllocationFrequency[]) => {
      this.allocationFrequencies = data;
    });

    this.lookupService.getScholarshipCategories().subscribe((data: ScholarshipCategory[]) => {
      this.scholarshipCategories = data;
    });

    this.lookupService.getEligibilties().subscribe((data: Eligibility[]) => {
      this.allEligibilities = data;
    });

    this.lookupService.getScholarshipProviders().subscribe((data: ScholarshipProvider[]) => {
      this.scholarshipProviders = data;
    });

    this.getMinimumDate();
    this.loadAvailableDraft();
    this.autosaveSubscription = interval(2000).subscribe(() => this.saveDraftIfChanged());
  }

  ngOnDestroy(): void {
    this.autosaveSubscription?.unsubscribe();
  }

  getMinimumDate(): void {
    const now = new Date();

    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');

    this.minDeadline = `${year}-${month}-${day}`;
  }

  get filteredTemplates(): ScholarshipTemplate[] {
    return this.templates;
  }

  get selectedTemplate(): ScholarshipTemplate {
    const template = this.templates.find((t: any) => t.id === this.selectedTemplateId);
    if (!template) {
      return null!;
    }
    return template;
  }

  get paginatedScholarships(): Scholarship[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.scholarships?.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.scholarships.length / this.pageSize));
  }

  get showingFrom(): number {
    return this.scholarships.length === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingTo(): number {
    return Math.min(this.currentPage * this.pageSize, this.scholarships.length);
  }

  onTemplateSelected(templateId: number) {
    const template = this.templates.find(t => t.id === templateId);

    if (!template) {
      this.errorMessage = 'Selected template not found.';
      return;
    }

    this.selectedTemplateId = templateId;
    this.country = template.country;
    this.countryId = template.countryId;
    this.templateDescription = template.description;
  }

  openCreateScholarship(): void {
    this.isEditing = false;
    this.editingScholarshipId = 0;
    this.resetForm();
    this.draftMessage = '';
    this.showScholarshipModal = true;
  }

  resumeDraft(): void {
    const draft = this.availableDraft?.payload;
    if (!draft) return;
    this.resetForm();
    Object.assign(this, draft);
    this.selectedEligibilities = [...(draft.selectedEligibilities ?? [])];
    this.country = this.countries.find(country => country.id === draft.countryId) ?? null!;
    if (this.selectedTemplateId) this.onTemplateSelected(this.selectedTemplateId);
    this.lastSavedDraftJson = JSON.stringify(this.draftPayload());
    this.isEditing = false;
    this.editingScholarshipId = 0;
    this.showScholarshipModal = true;
    this.draftMessage = 'Draft reopened. Changes are saved automatically.';
  }

  openEditScholarship(scholarship: Scholarship): void {
    this.isEditing = true;
    this.editingScholarshipId = scholarship.id;
    this.code = scholarship.code;
    this.title = scholarship.title;
    this.description = scholarship.description;
    this.overview = scholarship.overview;
    this.fundingAmount = scholarship.allocatedFunds;
    this.applicationProcess = scholarship.applicationProcess;
    this.applicationUrl = scholarship.applicationUrl;
    this.imageUrl = scholarship.imageUrl;
    this.deadline = scholarship.deadline ? String(scholarship.deadline).split('T')[0] : '';
    this.selectedTemplateId = scholarship.scholarshipTemplateId;
    this.selectedAllocationFrequencyId = scholarship.allocationFrequencyId;
    this.selectedCategoryId = scholarship.categoryId;
    this.providerId = scholarship.providerId;
    this.selectedEligibilities = scholarship.eligibilities?.map(e => e.eligibilityId) ?? [];

    if (scholarship.scholarshipTemplate) {
      this.country = scholarship.scholarshipTemplate.country;
      this.countryId = scholarship.scholarshipTemplate.countryId;
      this.templateDescription = scholarship.scholarshipTemplate.description;
    }

    this.showScholarshipModal = true;
  }

  closeModal(saveDraft = true): void {
    if (saveDraft) this.saveDraftIfChanged();
    this.showScholarshipModal = false;
    this.errorMessage = '';
    this.draftMessage = '';
    this.resetForm();
  }

  saveScholarship(): void {
    if (!this.isScholarshipFormValid()) {
      return;
    }

    if (this.isEditing) {
      const payload: UpdateScholarship = {
        id: this.editingScholarshipId,
        title: this.title.trim(),
        description: this.description.trim(),
        scholarshipTemplateId: this.selectedTemplateId,
        code: this.code.trim(),
        overview: this.overview.trim(),
        allocatedFunds: Number(this.fundingAmount) || 0,
        applicationProcess: this.applicationProcess.trim(),
        applicationUrl: this.applicationUrl.trim(),
        imageUrl: this.imageUrl.trim(),
        deadline: this.deadline ? new Date(this.deadline).toISOString() : null!,
        allocationFrequencyId: this.selectedAllocationFrequencyId,
        categoryId: this.selectedCategoryId,
        eligibilityIds: this.selectedEligibilities,
        providerId: this.providerId
      };

      this.scholarshipApi.updateScholarship(payload).subscribe(
        () => {
          this.saveMessage = `"${payload.title}" updated successfully.`;
          this.closeModal(false);
          this.scholarshipApi.invalidateCache();
          this.loadScholarships();
          this.deleteDraft();
        },
        (error) => {
          this.errorMessage = 'Failed to update scholarship. Please try again.';
          console.error(error);
        }
      );
    } else {
      const payload: CreateScholarship = {
        title: this.title.trim(),
        description: this.description.trim(),
        scholarshipTemplateId: this.selectedTemplateId,
        code: this.code.trim(),
        overview: this.overview.trim(),
        allocatedFunds: Number(this.fundingAmount) || 0,
        applicationProcess: this.applicationProcess.trim(),
        applicationUrl: this.applicationUrl.trim(),
        imageUrl: this.imageUrl.trim(),
        deadline: this.deadline ? new Date(this.deadline).toISOString() : null!,
        allocationFrequencyId: this.selectedAllocationFrequencyId,
        categoryId: this.selectedCategoryId,
        eligibilityIds: this.selectedEligibilities,
        providerId: this.providerId
      };

      this.scholarshipApi.saveScholarship(payload).subscribe(
        () => {
          this.saveMessage = `"${payload.title}" created successfully.`;
          this.closeModal(false);
          this.scholarshipApi.invalidateCache();
          this.loadScholarships();
          this.deleteDraft();
        },
        (error) => {
          this.errorMessage = 'Failed to create scholarship. Please try again.';
          console.error(error);
        }
      );
    }
  }

  isScholarshipFormValid(): boolean {
    this.errorMessage = '';

    if (!this.title.trim()) {
      this.errorMessage = 'Scholarship title is required.';
      return false;
    }

    if (!this.description.trim()) {
      this.errorMessage = 'Description is required.';
      return false;
    }

    const selectedTemplate = this.templates.find((t: ScholarshipTemplate) => t.id === this.selectedTemplateId);
    if (!selectedTemplate) {
      this.errorMessage = 'A valid application template must be selected.';
      return false;
    }

    if (selectedTemplate && selectedTemplate.country !== this.country) {
      this.errorMessage = 'The selected template does not match the scholarship country. Please choose a matching template.';
      return false;
    }

    if (!this.country || !this.countryId) {
      this.errorMessage = 'Country is required.';
      return false;
    }

    return true;
  }

  /**
   * Opens this scholarship's administration page: budget summary, awarded
   * students for the cycle and every application it received.
   */
  viewScholarship(scholarship: Scholarship): void {
    this.viewScholarshipRequest.emit(scholarship);
  }

  requestDelete(scholarshipId: number): void {
    this.scholarshipToDeleteId = scholarshipId;
    this.showDeleteConfirm = true;
  }

  cancelDelete(): void {
    this.scholarshipToDeleteId = 0;
    this.showDeleteConfirm = false;
  }

  confirmDelete(): void {
    if (!this.scholarshipToDeleteId) return;

    this.scholarshipApi.deleteScholarship(this.scholarshipToDeleteId).subscribe(
      () => {
        this.saveMessage = 'Scholarship deleted.';
        this.cancelDelete();
        this.scholarshipApi.invalidateCache();
        this.loadScholarships();
        setTimeout(() => { this.saveMessage = ''; }, 3000);
      },
      (error) => {
        this.errorMessage = 'Failed to delete scholarship. Please try again.';
        console.error(error);
      }
    );
  }

  /**
   * Human readable explanation of why a scholarship is open or closed, shown
   * next to the Open/Closed badge.
   */
  applicationWindowLabel(scholarship: Scholarship): string {
    if (scholarship.applicationWindow === 'FORCE_OPEN') {
      return 'Re-opened by admin';
    }

    if (scholarship.applicationWindow === 'FORCE_CLOSED') {
      return 'Closed by admin';
    }

    return scholarship.isAcceptingApplications ? 'Open until closing date' : 'Closing date passed';
  }

  /**
   * Opens or closes a scholarship's applications. FORCE_OPEN re-opens it even
   * after its closing date and FORCE_CLOSED closes it now; AUTOMATIC hands
   * control back to the closing date.
   */
  setApplicationWindow(scholarship: Scholarship, applicationWindow: ScholarshipApplicationWindow): void {
    this.savingApplicationWindowId = scholarship.id;

    this.scholarshipApi.setApplicationWindow(scholarship.id, applicationWindow).subscribe(
      () => {
        this.savingApplicationWindowId = 0;
        this.saveMessage = applicationWindow === 'FORCE_OPEN'
          ? `Applications for "${scholarship.title}" are now open.`
          : applicationWindow === 'FORCE_CLOSED'
            ? `Applications for "${scholarship.title}" are now closed.`
            : `Applications for "${scholarship.title}" now follow the closing date.`;
        this.scholarshipApi.invalidateCache();
        this.loadScholarships();
        setTimeout(() => { this.saveMessage = ''; }, 3000);
      },
      (error) => {
        this.savingApplicationWindowId = 0;
        this.errorMessage = 'Failed to update the applications state. Please try again.';
        console.error(error);
      }
    );
  }

  getRequiredDocCount(template: ScholarshipTemplate): number {
    return this.getDistinctDocumentTypeNames(template).length;
  }

  getDistinctDocumentTypeNames(template: ScholarshipTemplate): string[] {
    return [
      ...new Set(
        template.documentTypeFormats.map(
          (d: DocumentTypeFormat) => d.documentType.name
        )
      )
    ];
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages)
      return;

    this.currentPage = page;
  }

  private loadScholarships(): void {
    this.scholarshipApi.getScholarships().subscribe((data: Scholarship[]) => {
      this.scholarships = data;
      this.scholarshipsCount = data?.length ?? 0;
    });
  }

  private resetForm(): void {
    this.code = '';
    this.title = '';
    this.country = null!;
    this.fundingAmount = null!;
    this.applicationProcess = '';
    this.deadline = '';
    this.applicationUrl = '';
    this.imageUrl = '';
    this.description = '';
    this.selectedTemplateId = 0;
    this.countryId = 0;
    this.overview = '';
    this.selectedEligibilities = [];
    this.selectedAllocationFrequencyId = 0;
    this.selectedCategoryId = 0;
    this.providerId = 0;
  }

  saveDraft(): void {
    this.saveDraftIfChanged(true);
  }

  private saveDraftIfChanged(force = false): void {
    if (!this.showScholarshipModal || this.isEditing || this.isSavingDraft) return;
    const payload = this.draftPayload();
    if (!force && !this.hasScholarshipDraftContent(payload)) return;
    const payloadJson = JSON.stringify(payload);
    if (!force && payloadJson === this.lastSavedDraftJson) return;

    this.isSavingDraft = true;
    this.draftApi.save('scholarship', this.title.trim() || 'Untitled scholarship', payload).subscribe({
      next: () => {
        this.lastSavedDraftJson = payloadJson;
        this.availableDraft = { displayName: this.title.trim() || 'Untitled scholarship', payload, modifiedDate: new Date().toISOString() };
        this.draftMessage = 'Draft saved';
        this.isSavingDraft = false;
      },
      error: () => { this.isSavingDraft = false; },
    });
  }

  private loadAvailableDraft(): void {
    this.draftApi.get<ScholarshipDraftPayload>('scholarship').subscribe({ next: draft => this.availableDraft = draft, error: () => undefined });
  }

  private deleteDraft(): void {
    this.draftApi.delete('scholarship').subscribe({ next: () => { this.availableDraft = undefined; this.lastSavedDraftJson = ''; }, error: () => undefined });
  }

  private draftPayload(): ScholarshipDraftPayload {
    return {
      code: this.code, title: this.title, description: this.description, overview: this.overview,
      selectedTemplateId: this.selectedTemplateId, countryId: this.countryId, fundingAmount: this.fundingAmount,
      applicationProcess: this.applicationProcess, scholarshipLevel: this.scholarshipLevel, deadline: this.deadline,
      applicationUrl: this.applicationUrl, imageUrl: this.imageUrl, selectedAllocationFrequencyId: this.selectedAllocationFrequencyId,
      selectedCategoryId: this.selectedCategoryId, providerId: this.providerId, selectedEligibilities: this.selectedEligibilities,
    };
  }

  private hasScholarshipDraftContent(payload: ScholarshipDraftPayload): boolean {
    return !!(payload.title || payload.code || payload.description || payload.selectedTemplateId || payload.overview);
  }

  onImageSelected(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        this.imageUrl = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  }
}

interface ScholarshipDraftPayload {
  code: string;
  title: string;
  description: string;
  overview: string;
  selectedTemplateId: number;
  countryId: number;
  fundingAmount: number;
  applicationProcess: string;
  scholarshipLevel: string;
  deadline: string;
  applicationUrl: string;
  imageUrl: string;
  selectedAllocationFrequencyId: number;
  selectedCategoryId: number;
  providerId: number;
  selectedEligibilities: number[];
}
